import React from 'react';
import PropTypes from 'prop-types';
import { Tabs, Tab } from '@mui/material';
import { tabsA11yProps } from 'common/utils/general';

/**
 * Toggle segmentado tipo "pill" (referencia MySchoolUAE Family Profile): track redondeado
 * con la opción activa como píldora elevada que se desliza. Construido sobre MUI Tabs para
 * heredar teclado/ARIA. La píldora es el propio indicador del Tabs con estilos de superficie.
 *
 * Dimensionado: el track se ajusta al contenido y NUNCA recorta un label (antes, con
 * `variant="fullWidth"` y ancho fijo, "Programa de entrenamiento" salía cortado). Si la fila
 * no cabe, se desplaza en horizontal con el dedo en vez de encogerse.
 */
function PillTabs({
  value,
  onChange,
  tabs,
  maxWidth = '100%',
  fullWidth = false,
  centered = false,
  ariaLabel = undefined,
  hasPanels = false,
  sx = undefined
}) {
  return (
    <Tabs
      value={value}
      onChange={onChange}
      variant="scrollable"
      scrollButtons={false}
      aria-label={ariaLabel}
      TabIndicatorProps={{
        sx: {
          top: 0,
          bottom: 0,
          height: 'auto',
          borderRadius: 999,
          zIndex: 0,
          bgcolor: 'surface.raised',
          border: '1px solid',
          borderColor: 'hairline.main',
          boxShadow: 'var(--otx-card-shadow)',
          transition:
            'left 250ms cubic-bezier(0.32, 0.72, 0, 1), width 250ms cubic-bezier(0.32, 0.72, 0, 1)'
        }
      }}
      sx={{
        minHeight: 44,
        p: 0.5,
        borderRadius: 999,
        bgcolor: 'surface.overlay',
        border: '1px solid',
        borderColor: 'hairline.main',
        // Hugea el contenido; el tope lo pone el contenedor, no un ancho inventado.
        width: fullWidth ? '100%' : 'fit-content',
        maxWidth,
        ...(centered && { mx: 'auto' }),
        // Scroll táctil silencioso: sin barra visible ni flechas que rompan la píldora.
        '& .MuiTabs-scroller': {
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' }
        },
        '& .MuiTabs-flexContainer': { position: 'relative', zIndex: 1 },
        '& .MuiTab-root': {
          // Crece para repartir el espacio sobrante, pero jamás por debajo de su texto.
          flex: '1 1 auto',
          minWidth: 'max-content',
          minHeight: 36,
          py: 0.75,
          px: { xs: 1.75, sm: 2.5 },
          textTransform: 'none',
          fontWeight: 700,
          fontSize: { xs: '0.8125rem', sm: '0.875rem' },
          borderRadius: 999,
          color: 'text.secondary',
          transition: 'color 250ms cubic-bezier(0.32, 0.72, 0, 1)',
          whiteSpace: 'nowrap',
          '&.Mui-selected': { color: 'selected.fg' },
          '&:active': { transform: 'scale(0.98)' },
          // En pantallas muy angostas los iconos roban espacio al label: solo texto en xs.
          '& .MuiSvgIcon-root': { fontSize: 18, display: { xs: 'none', sm: 'inline-flex' } }
        },
        ...sx
      }}>
      {tabs.map((tabItem, index) => {
        // `aria-controls` solo si de verdad existe un role="tabpanel" con ese id: apuntar a un
        // elemento inexistente confunde a los lectores de pantalla más que no declararlo.
        const { id, 'aria-controls': controls } = tabsA11yProps(index);
        return (
          <Tab
            key={tabItem.key || tabItem.label}
            label={tabItem.label}
            icon={tabItem.icon}
            iconPosition="start"
            id={id}
            aria-controls={hasPanels ? controls : undefined}
          />
        );
      })}
    </Tabs>
  );
}

PillTabs.propTypes = {
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.node.isRequired,
      icon: PropTypes.node
    })
  ).isRequired,
  maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  fullWidth: PropTypes.bool,
  centered: PropTypes.bool,
  ariaLabel: PropTypes.string,
  /** true solo si la pantalla renderiza un `role="tabpanel"` con id `tabpanel-{index}`. */
  hasPanels: PropTypes.bool,
  sx: PropTypes.object
};

export default PillTabs;
